import { useAnalytics } from "@accurx/analytics";
import { Ds } from "@accurx/design";
import { SkeletonLoader } from "@accurx/inbox-design-library";
import { useAppointmentsConfigurationQuery } from "@accurx/self-book";
import { isPast } from "date-fns";
import type {
    ScheduleSendAt,
    ScheduleSendItemsType,
} from "domains/message-component/components/Compose/components/ScheduleSend/types/ScheduleSend.types";
import { getSelectedDateTimeUK } from "domains/message-component/components/Compose/components/ScheduleSend/utils/getSelectedDateTimeUK";
import {
    isPresetDateType,
    mapSendAtTypeToScheduleSendItemsType,
} from "domains/message-component/components/Compose/components/ScheduleSend/utils/mapSendAtToScheduleSendItems";
import { useCompose } from "domains/message-component/context";
import { usePresetDateOptions } from "domains/message-component/hooks/usePresetDateOptions";

type ScheduleSendProps = {
    onConfirmScheduleSend: (sendAt: ScheduleSendAt) => void;
    onClickCustomDate: () => void;
};

export const ScheduleSendItems = ({
    onConfirmScheduleSend,
    onClickCustomDate,
}: ScheduleSendProps) => {
    const track = useAnalytics();

    const { state } = useCompose();
    const { data: appointmentConfiguration } =
        useAppointmentsConfigurationQuery({
            enabled: !!state.selfBookLink,
        });

    const showCustomDateTimeOption =
        !!appointmentConfiguration?.selfBookSchedulingWeeksIntoFuture ||
        !state.selfBookLink;

    const { status, options, nextWorkingDay } = usePresetDateOptions();

    const trackItemClick = ({
        scheduleType,
        scheduleTime,
    }: {
        scheduleType: "Now" | "Custom";
        scheduleTime: string | null;
    }) => {
        const scheduledTimestampUtc = scheduleTime
            ? new Date(scheduleTime).toUTCString()
            : undefined;

        track("ConversationSchedule MenuItem Click", {
            indexMessage: 0,
            templateName: state.template.value?.title,
            scheduleType,
            scheduledTimestampUtc,
        });
    };

    const hasSelectedDatePassed = state.sendAt
        ? isPast(new Date(state.sendAt.sendAtDateTime))
        : false;

    const currentValue: ScheduleSendItemsType | undefined =
        hasSelectedDatePassed
            ? undefined
            : mapSendAtTypeToScheduleSendItemsType(state.sendAt?.type);

    const onSelectScheduleOptions = (value: string) => {
        if (value === "custom") {
            trackItemClick({ scheduleType: "Custom", scheduleTime: null });
            onClickCustomDate();
            return;
        }

        if (value === currentValue) return;

        if (value === "now" || !isPresetDateType(value)) {
            trackItemClick({ scheduleType: "Now", scheduleTime: null });
            onConfirmScheduleSend(null);
            return;
        }

        const sendAtDateTime = getSelectedDateTimeUK({
            type: value,
            nextWorkingDay,
        });

        const sendAt = sendAtDateTime ? { type: value, sendAtDateTime } : null;

        trackItemClick({
            scheduleType: "Custom",
            scheduleTime: sendAtDateTime,
        });
        onConfirmScheduleSend(sendAt);
    };

    return (
        <>
            <Ds.DropdownMenu.RadioGroup
                value={currentValue}
                onValueChange={(item) => {
                    onSelectScheduleOptions(item);
                }}
            >
                <Ds.DropdownMenu.RadioItem aria-label="Now" value="now">
                    Now
                </Ds.DropdownMenu.RadioItem>
                {status === "loading" ? (
                    <Ds.Box p="1">
                        <SkeletonLoader width="120px" />
                    </Ds.Box>
                ) : (
                    options.map((item) => (
                        <Ds.DropdownMenu.RadioItem
                            key={item.value}
                            aria-label={item.text}
                            value={item.value}
                        >
                            {item.text}
                        </Ds.DropdownMenu.RadioItem>
                    ))
                )}
                {showCustomDateTimeOption && (
                    <Ds.DropdownMenu.RadioItem
                        value="custom"
                        aria-label="Custom date and time"
                    >
                        Custom date and time
                    </Ds.DropdownMenu.RadioItem>
                )}
            </Ds.DropdownMenu.RadioGroup>
        </>
    );
};
